import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { TextSizeControl } from './accessibility/TextSizeControl';

export const PublicNavigation: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { state, dispatch } = useApp();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const navigationItems = [
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'Find Caregivers', path: '/find-caregivers' },
    { label: 'Become a Caregiver', path: '/auth?role=caregiver' },
  ];

  const drawer = (
      <Box sx={{ width: 250 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Box
            component="img"
            src="/logos/logo-wide.png"
            alt="Careva"
            sx={{
              height: 32,
              width: 'auto',
            }}
          />
          <IconButton onClick={handleDrawerToggle}>
            <CloseIcon />
          </IconButton>
        </Box>
      <List>
        {navigationItems.map((item) => (
          <ListItem 
            key={item.label} 
            button 
            onClick={() => handleNavigation(item.path)}
            sx={{ 
              '&:hover': { 
                backgroundColor: theme.palette.primary.light,
                '& .MuiListItemText-primary': {
                  color: 'white',
                }
              }
            }}
          >
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
        <ListItem sx={{ pt: 2, flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
          <TextSizeControl
            value={state.textSize}
            onChange={(size) => dispatch({ type: 'SET_TEXT_SIZE', payload: size })}
            testId="text-size-control-mobile"
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => handleNavigation('/auth')}
              sx={{ flex: 1 }}
            >
              Sign In
            </Button>
            <Button
              variant="contained"
              onClick={() => handleNavigation('/auth')}
              sx={{ flex: 1 }}
            >
              Get Started
            </Button>
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box
              component="img"
              src="/logos/logo-wide.png"
              alt="Careva"
              onClick={() => navigate('/')}
              sx={{
                height: 40,
                width: 'auto',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                }
              }}
            />

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.label}
                    color="inherit"
                    onClick={() => handleNavigation(item.path)}
                    sx={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.light,
                        color: 'white',
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <TextSizeControl
                  value={state.textSize}
                  onChange={(size) => dispatch({ type: 'SET_TEXT_SIZE', payload: size })}
                  compact
                />
                <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => handleNavigation('/auth')}
                    sx={{ 
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        backgroundColor: theme.palette.primary.light,
                        color: 'white',
                      }
                    }}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => handleNavigation('/auth')}
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      }
                    }}
                  >
                    Get Started
                  </Button>
                </Box>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ color: theme.palette.primary.main }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 250,
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};
